/**
 * TRAC-862 — dev-only stream fault injection.
 *
 * Why this exists: every bug in this ticket lived in how the client reacts to a
 * BROKEN stream, and the only way we could produce one was `kill -9` on the
 * agent. That gives exactly one failure shape (agent unreachable), reproduces
 * non-deterministically — the same kill sometimes yielded a fast `response_error`
 * and sometimes a silent stream — and can't produce the shape that actually
 * caused the incident (a crash *after* content had already been sent).
 *
 * These scripts emit events into the SAME listener the real SSE transport feeds,
 * so the reader loop, idle watchdog, heartbeat handling, retry scheduler and
 * overlay all run for real. Only the wire is fake.
 *
 * `remaining` is the point of the design: set it to 2 and the first two attempts
 * fail while the third succeeds, so you can watch a retry actually recover. Set
 * it high and the chain exhausts into the emergency overlay.
 *
 * Dev/QA only — armed from the Dev Settings drawer, stored in sessionStorage so
 * it survives the reloads and resends a retry chain performs.
 */

import { logger } from '../utils/logger';

export type FaultShape =
  /** Nothing ever arrives. The watchdog is the only thing that can end this. */
  | 'silent'
  /** Only connection heartbeats — the agent is dead but the socket is warm.
   *  This is the shape that used to spin forever, because heartbeats reset the
   *  idle timer. Compressed to 5s (prod sends every 30s) so a test isn't slow. */
  | 'heartbeat-only'
  /** Answer streams, THEN the turn dies with no `done` — the server.py:631
   *  AttributeError shape. `kill -9` cannot reproduce this one. */
  | 'crash-midstream'
  /** Immediate error events, one per sanitized class. */
  | 'error-generic'
  | 'error-throttle'
  | 'error-unavailable'
  /** Slow but genuinely alive: keepalives past the idle timeout, then a real
   *  answer. Proves the keepalive prevents a false timeout on a slow turn. */
  | 'slow-ok';

export interface FaultPlan {
  shape: FaultShape;
  /** Turns still to be failed. Decremented on each consume; 0 disarms. */
  remaining: number;
}

const STORAGE_KEY = 'traci_dev_stream_fault';

export const FAULT_SHAPES: { value: FaultShape; label: string; blurb: string }[] = [
  { value: 'silent', label: 'Silent stream', blurb: 'No events at all — watchdog must fire' },
  { value: 'heartbeat-only', label: 'Heartbeat only', blurb: 'Socket warm, agent dead (the spinner bug)' },
  { value: 'crash-midstream', label: 'Crash mid-stream', blurb: 'Answer, then die with no done' },
  { value: 'error-generic', label: 'Generic error', blurb: '"Something went wrong"' },
  { value: 'error-throttle', label: 'Throttled', blurb: 'ThrottlingException' },
  { value: 'error-unavailable', label: '503 unavailable', blurb: 'Service Unavailable' },
  { value: 'slow-ok', label: 'Slow but alive', blurb: 'Keepalives past 45s, then answers' },
];

export function armFault(shape: FaultShape, remaining: number): void {
  const plan: FaultPlan = { shape, remaining: Math.max(1, remaining) };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
  logger.warn(`[TRAC-862] fault armed: ${shape} for ${plan.remaining} turn(s)`);
}

export function disarmFault(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function peekFault(): FaultPlan | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const plan = JSON.parse(raw) as FaultPlan;
    return plan?.remaining > 0 ? plan : null;
  } catch {
    return null;
  }
}

/** Take one turn off the plan. Returns the shape to play, or null if unarmed. */
export function consumeFault(): FaultShape | null {
  const plan = peekFault();
  if (!plan) return null;
  const left = plan.remaining - 1;
  if (left > 0) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...plan, remaining: left }));
  } else {
    disarmFault();
  }
  logger.warn(`[TRAC-862] injecting fault: ${plan.shape} (${left} turn(s) left after this)`);
  return plan.shape;
}

type Emit = (event: Record<string, unknown>) => void;

/**
 * Play a fault script. Returns a cleanup function that stops any pending timers —
 * the caller MUST invoke it when the turn ends, or a script (heartbeat-only in
 * particular) keeps emitting into a listener that has moved on.
 */
export function playFault(shape: FaultShape, emit: Emit): () => void {
  const timers: ReturnType<typeof setTimeout>[] = [];
  const intervals: ReturnType<typeof setInterval>[] = [];
  const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
  const every = (ms: number, fn: () => void) => intervals.push(setInterval(fn, ms));

  const messageId = `fault-${Date.now()}`;
  let seq = 1;
  const start = () => emit({ type: 'response_start', data: { messageId }, sequence: seq++ });
  const err = (message: string) =>
    emit({ type: 'response_error', data: { error: message }, sequence: seq++ });
  const chunk = (text: string) =>
    emit({ type: 'response_chunk', data: { chunk: text }, sequence: seq++ });

  switch (shape) {
    case 'silent':
      // Deliberately nothing. Not even response_start — the stream opens and
      // never speaks.
      break;

    case 'heartbeat-only':
      at(100, start);
      every(5_000, () => emit({ type: 'heartbeat', data: {}, sequence: seq++ }));
      break;

    case 'crash-midstream':
      at(100, start);
      at(600, () => chunk('Tire rotation helps all four tires wear evenly — '));
      at(1_200, () => chunk('the usual interval is every 5,000 to 7,000 miles.'));
      // The crash: an error arrives after the answer, and `done` never does.
      at(1_800, () => err("'NoneType' object has no attribute 'get'"));
      break;

    case 'error-generic':
      at(100, start);
      at(700, () => err('Agent invocation failed'));
      break;

    case 'error-throttle':
      at(100, start);
      at(700, () => err('ThrottlingException: rate exceeded'));
      break;

    case 'error-unavailable':
      at(100, start);
      at(700, () => err('Service Unavailable'));
      break;

    case 'slow-ok': {
      at(100, start);
      // Keepalives well past STREAM_IDLE_TIMEOUT_MS. If the client ever counts
      // these as "no activity", this turn dies and the test fails loudly.
      every(5_000, () => emit({ type: 'agent_keepalive', data: {}, sequence: seq++ }));
      at(60_000, () => {
        chunk('That took a while, but here it is: rotate every 5,000 to 7,000 miles.');
        emit({ type: 'response_complete', data: { messageId }, sequence: seq++ });
      });
      break;
    }
  }

  return () => {
    timers.forEach(clearTimeout);
    intervals.forEach(clearInterval);
  };
}
